import axios from 'axios';
import type { MetaAccount } from '../settings/types';

const GRAPH = 'https://graph.facebook.com/v21.0';

export type MetaPageInsights = {
  pageName: string;
  fanCount?: number;
  followersCount?: number;
  instagramUsername?: string;
  instagramFollowers?: number;
};

export async function testMetaAccountConnection(
  account: MetaAccount,
  appId?: string
): Promise<{ ok: true; label: string } | { ok: false; message: string }> {
  const token = account.accessToken.trim();
  if (!token) {
    return { ok: false, message: 'Access token is required' };
  }
  try {
    if (account.pageId.trim()) {
      const { data } = await axios.get<{ name?: string; fan_count?: number; followers_count?: number }>(
        `${GRAPH}/${account.pageId.trim()}`,
        { params: { fields: 'name,fan_count,followers_count', access_token: token } }
      );
      const ig =
        account.platform !== 'facebook' && account.instagramBusinessId.trim()
          ? await axios
              .get<{ username?: string; followers_count?: number }>(
                `${GRAPH}/${account.instagramBusinessId.trim()}`,
                { params: { fields: 'username,followers_count', access_token: token } }
              )
              .then((r) => r.data)
              .catch(() => null)
          : null;
      const parts = [data.name || 'Page connected'];
      if (data.fan_count != null) {
        parts.push(`${data.fan_count.toLocaleString()} FB fans`);
      }
      if (ig?.username) {
        parts.push(`@${ig.username}`);
      }
      return { ok: true, label: parts.join(' · ') };
    }
    const { data } = await axios.get<{ name?: string; id?: string }>(`${GRAPH}/me`, {
      params: { fields: 'name,id', access_token: token },
    });
    return { ok: true, label: data.name ? `Token valid · ${data.name}` : 'Token valid' };
  } catch (e) {
    const msg = axios.isAxiosError(e)
      ? e.response?.data?.error?.message || e.message
      : 'Connection failed';
    return { ok: false, message: String(msg) };
  }
}

export async function fetchMetaPageInsights(account: MetaAccount): Promise<MetaPageInsights | null> {
  const token = account.accessToken.trim();
  if (!token || !account.pageId.trim()) {
    return null;
  }
  const { data } = await axios.get<{ name?: string; fan_count?: number; followers_count?: number }>(
    `${GRAPH}/${account.pageId.trim()}`,
    { params: { fields: 'name,fan_count,followers_count', access_token: token } }
  );
  let ig: { username?: string; followers_count?: number } | null = null;
  if (account.instagramBusinessId.trim()) {
    try {
      const r = await axios.get(`${GRAPH}/${account.instagramBusinessId.trim()}`, {
        params: { fields: 'username,followers_count', access_token: token },
      });
      ig = r.data;
    } catch {
      ig = null;
    }
  }
  return {
    pageName: data.name || account.label,
    fanCount: data.fan_count,
    followersCount: data.followers_count,
    instagramUsername: ig?.username,
    instagramFollowers: ig?.followers_count,
  };
}
